import { apiErrorMessage } from '@agos/api-client'
import { usePostUploadImage } from '@agos/api-client/files'
import { useGetProducts } from '@agos/api-client/menu'
import type { Product, PutHomepageModulesBulkBody } from '@agos/api-client/model'
import {
  getGetHomepageModulesQueryKey,
  useDeleteHomepageModulesKey,
  useGetHomepageModules,
  usePostHomepageModules,
  usePutHomepageModulesBulk,
} from '@agos/api-client/marketing'
import {
  useGetSettingsSocial,
  usePutSettingsSocial,
} from '@agos/api-client/platform'
import { ACCENT_COLORS, T, THEME_FONTS, formatCount, tt, useAuth, useTheme } from '@agos/shared'
import {
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Layers,
  Moon,
  Palette,
  Plus,
  Save,
  Settings,
  Trash2,
  Type,
  Upload,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  MODULE_SCHEMAS,
  addableKeys,
  blockCounts,
  blocksOf,
  bulkPayload,
  editorState,
  isDirty,
  moduleLabel,
  move,
  schemaFor,
  setContent,
  toggle,
  imageProblem,
  imageProblemMessage,
  productGroups,
  productIdsOf,
  selectionNote,
  toggleProductId,
  type Block,
  type HomepageModule,
  type ModuleField,
  type PickerProduct,
} from './homepage-modules'
import {
  LOGO_HEIGHT_MAX,
  LOGO_HEIGHT_MIN,
  PAGE_DESCRIPTION_MAX,
  PAGE_TITLE_MAX,
  clampLogoHeight,
  divergences,
  fieldLabel,
  hasUrlProblem,
  socialOf,
  socialPayload,
  toEmbedUrl,
  urlProblemMessage,
  urlProblems,
  type SocialSettings,
  type UrlField,
  type UrlProblem,
} from './site-identity'

/**
 * `/design` — how the customer site looks and what its homepage holds.
 *
 * **FB-126.** Legacy's save fires `PUT /settings/social` inside `try { … } catch {}` and then says
 * *Nastavenia uložené* whether or not anything was saved — while having already written the new values
 * into the theme context. `GET /settings/social` admits `main_admin` and `admin`; **`PUT` admits
 * `main_admin` only**, so an `admin` is told their change saved and it did not.
 *
 * **FB-127.** Two controls for the homepage's contents, and one is wired to nothing.
 * `settings/design.activeModules` is a list of Slovak labels — read by no customer app, no manager page
 * and no API consumer, grepped across the whole legacy tree — while the homepage is actually composed
 * from `homepage_modules`. On this data the labels name four blocks and ten are enabled.
 *
 * **FB-128.** `ModuleContentEditor` is twelve `if (moduleKey === …)` branches ending in `return null`,
 * so a block nobody has described opens to a blank editor, indistinguishable from the two blocks that
 * genuinely have nothing to configure.
 *
 * **FB-129.** Four fields live in both `social_settings` and `design_settings`, written by two requests
 * from one button.
 *
 * Ported from legacy `pages/design-settings.tsx` (1 752 lines).
 */

type TabId = 'look' | 'site' | 'homepage'

export function DesignSettingsPage() {
  const [tab, setTab] = useState<TabId>('look')

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Dizajn stránky')}
        description={tt('Vzhľad, texty a bloky zákazníckeho webu')}
        icon={<Palette className="w-5 h-5" />}
      />

      <TabGroup
        variant="bar"
        tabs={[
          { id: 'look', label: tt('Vzhľad'), icon: Palette },
          { id: 'site', label: tt('Web a kontakty'), icon: Settings },
          { id: 'homepage', label: tt('Úvodná stránka'), icon: Layers },
        ]}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      {tab === 'look' && <LookTab />}
      {tab === 'site' && <SiteTab />}
      {tab === 'homepage' && <HomepageTab />}
    </div>
  )
}

// ─── vzhľad ──────────────────────────────────────────────────────────────────

function LookTab() {
  const theme = useTheme()

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground" data-look-autosave>
        {/* The theme context debounces its own `PUT /settings/design`, so there is no save button here
            and no toast claiming one. */}
        <T>Zmeny vzhľadu sa ukladajú samy - nič sa tu nepotvrdzuje tlačidlom.</T>
      </p>

      <Card title={tt('Režim')} icon={<Eye className="w-4 h-4 text-primary" />}>
        <div className="flex gap-2 flex-wrap" data-theme-mode>
          {(['light', 'dark', 'auto'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              data-mode={mode}
              aria-pressed={theme.mode === mode}
              onClick={() => theme.setMode(mode)}
              className={cn(
                'px-4 py-2 rounded-xl border text-sm font-semibold',
                theme.mode === mode
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              {mode === 'light' ? tt('Svetlý') : mode === 'dark' ? tt('Tmavý') : tt('Podľa systému')}
            </button>
          ))}
        </div>
        {/* §9.288, FB-448 - this page is described as the customer website's appearance and this one
          * card is not. `mode` is `theme-context`'s manager axis.
          *
          * **The second sentence changed with §9.303.** It used to end *"zákaznícka stránka je zatiaľ
          * vždy svetlá"*, which was true and is not any more: the card below sets the customer site's
          * scheme. A note that goes on describing a limitation somebody has since lifted is worse
          * than no note, because it is the one thing on the card a reader will believe. */}
        <p className="text-[11px] text-amber-500" data-mode-scope>
          <T>
            Týka sa vzhľadu manažéra, nie zákazníckeho webu - schému zákazníckej stránky nastavíte
            nižšie.
          </T>
        </p>
      </Card>

      {/* §9.303, owner 2026-09-09: *"its per franchise (so implement it also there)"*. The scheme the
        * customers see, which is a different axis from the card above - any theme can run dark, and
        * this is where the owner already looked for it. */}
      <Card
        title={tt('Schéma zákazníckej stránky')}
        icon={<Moon className="w-4 h-4 text-primary" />}
      >
        <div className="flex gap-2 flex-wrap" data-customer-scheme={theme.colorScheme}>
          {(['light', 'dark'] as const).map((scheme) => (
            <button
              key={scheme}
              type="button"
              data-scheme-option={scheme}
              aria-pressed={theme.colorScheme === scheme}
              onClick={() => theme.setColorScheme(scheme)}
              className={cn(
                'px-4 py-2 rounded-xl border text-sm font-semibold',
                theme.colorScheme === scheme
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              {scheme === 'light' ? tt('Svetlá') : tt('Tmavá')}
            </button>
          ))}
        </div>
        {/* No *Podľa systému*, deliberately - the owner's own reason, from the legacy design this
          * ports: a franchise whose brand is black must look black on every device. */}
        <p className="text-[11px] text-muted-foreground">
          <T>
            Platí pre celú zákaznícku stránku, pre všetkých návštevníkov. Farba značky zostáva rovnaká
            v oboch schémach.
          </T>
        </p>
      </Card>

      <Card title={tt('Farba')} icon={<Palette className="w-4 h-4 text-primary" />}>
        <div className="flex gap-2 flex-wrap" data-theme-accent>
          {ACCENT_COLORS.map((accent) => (
            <button
              key={accent.id}
              type="button"
              data-accent={accent.id}
              aria-label={accent.name}
              aria-pressed={theme.accentId === accent.id}
              onClick={() => theme.setAccent(accent.id)}
              className={cn(
                'w-10 h-10 rounded-xl border-2',
                theme.accentId === accent.id ? 'border-foreground' : 'border-transparent',
              )}
              style={{ background: `hsl(${accent.hsl})` }}
            />
          ))}
        </div>
      </Card>

      <Card title={tt('Písmo')} icon={<Type className="w-4 h-4 text-primary" />}>
        <div className="flex gap-2 flex-wrap" data-theme-font>
          {THEME_FONTS.map((font) => (
            <button
              key={font}
              type="button"
              data-font={font}
              aria-pressed={theme.font === font}
              onClick={() => theme.setFont(font)}
              className={cn(
                'px-4 py-2 rounded-xl border text-sm font-semibold',
                theme.font === font
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground',
              )}
            >
              {font}
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}

// ─── web a kontakty ──────────────────────────────────────────────────────────

const FIELD =
  'px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-full'

/**
 * The loader waits; the form below is seeded once, from a prop.
 *
 * Seeding a form inside an effect means a render with the wrong values and then a second one, and it
 * fights whoever is already typing. Mounting the form only once the data is here is the same rule the
 * invoice editor follows.
 */
function SiteTab() {
  const { data, isLoading } = useGetSettingsSocial()
  if (isLoading || data === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  return <SiteForm settings={data} />
}

function SiteForm({ settings }: { settings: SocialSettings }) {
  const theme = useTheme()
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [social, setSocial] = useState(() => socialOf(settings))

  const [advanced, setAdvanced] = useState(() => ({
    pageTitle: theme.pageTitle,
    pageDescription: theme.pageDescription,
    logoHeight: theme.logoHeight,
    homePageUrl: theme.homePageUrl,
  }))

  const problems = urlProblems({
    facebookUrl: social.facebookUrl,
    instagramUrl: social.instagramUrl,
    tiktokUrl: social.tiktokUrl,
    googleReviewsUrl: social.googleReviewsUrl,
    mapEmbedUrl: social.mapEmbedUrl,
    homePageUrl: advanced.homePageUrl,
  })

  const drift = divergences(social, {
    restaurantName: theme.restaurantName,
    facebookUrl: theme.facebookUrl,
    instagramUrl: theme.instagramUrl,
    mapEmbedUrl: theme.mapEmbedUrl,
  })

  // `PUT /settings/social` is `main_admin` only, while the GET admits `admin` too.
  const maySave = user?.systemRole === 'main_admin'

  const saveSocial = usePutSettingsSocial({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['/api/v1/settings/social'] })
        // Only now does the theme context get the shared fields, so a failure leaves nothing behind.
        theme.setRestaurantName(social.restaurantName.trim())
        theme.setAdvanced({
          ...advanced,
          logoHeight: clampLogoHeight(advanced.logoHeight),
          facebookUrl: social.facebookUrl.trim(),
          instagramUrl: social.instagramUrl.trim(),
          mapEmbedUrl: social.mapEmbedUrl.trim(),
        })
        toast({ title: tt('Nastavenia uložené') })
      },
      // FB-126: legacy swallows this and says it saved anyway.
      // §9.385 - the design write validates colours and module keys, and says which it refused.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(
            caught,
            tt('Na serveri sa nič nezmenilo. Nič sa nezobrazuje ako uložené.'),
          ),
        }),
    },
  })

  const set = <K extends keyof typeof social>(key: K, value: string) =>
    setSocial((current) => ({ ...current, [key]: value }))

  const save = () => {
    if (hasUrlProblem(problems)) {
      toast({ variant: 'destructive', title: tt('Opravte zvýraznené polia') })
      return
    }
    // A share link is converted to one an iframe accepts, and the field shows the result.
    const embedded = toEmbedUrl(social.mapEmbedUrl) ?? social.mapEmbedUrl
    const next = { ...social, mapEmbedUrl: embedded }
    setSocial(next)
    saveSocial.mutate({ data: socialPayload(next) })
  }

  return (
    <div className="space-y-4">
      {!maySave && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {/* Legacy lets an admin type, press save, and be told it saved while the request 403s. */}
          <T>
            Tieto nastavenia môže uložiť len hlavný administrátor. Meniť polia môžete, uložiť ich
            nedokážete - server zmenu odmietne.
          </T>
        </p>
      )}

      {drift.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs space-y-1" data-settings-drift>
          {/* FB-129: two sections hold the same four fields, written by two different requests. */}
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Dve miesta uložených nastavení sa rozišli</T>
          </p>
          {drift.map((entry) => (
            <p key={entry.field} className="text-muted-foreground" data-drift={entry.field}>
              <strong>{fieldLabel(entry.field)}</strong>: <T>kontakty</T> „{entry.social || '-'}“,{' '}
              <T>dizajn</T> „{entry.design || '-'}“
            </p>
          ))}
          <p className="text-muted-foreground">
            <T>Uložením tejto stránky sa zjednotia na hodnotu z polí nižšie.</T>
          </p>
        </div>
      )}

      <Card title={tt('Reštaurácia')} icon={<Settings className="w-4 h-4 text-primary" />}>
        <Labelled label={tt('Názov reštaurácie')} htmlFor="site-name">
          <input
            id="site-name"
            className={FIELD}
            value={social.restaurantName}
            onChange={(event) => set('restaurantName', event.target.value)}
          />
        </Labelled>
        <Labelled label={tt('Adresa v pätičke')} htmlFor="site-address">
          <input
            id="site-address"
            className={FIELD}
            value={social.address}
            onChange={(event) => set('address', event.target.value)}
          />
        </Labelled>
      </Card>

      <Card title={tt('Vo vyhľadávaní a v prehliadači')} icon={<Type className="w-4 h-4 text-primary" />}>
        <Labelled label={tt('Titulok stránky')} htmlFor="site-title">
          <input
            id="site-title"
            className={FIELD}
            value={advanced.pageTitle}
            onChange={(event) => setAdvanced((current) => ({ ...current, pageTitle: event.target.value }))}
          />
          <Counter length={advanced.pageTitle.length} max={PAGE_TITLE_MAX} />
        </Labelled>
        <Labelled label={tt('Popis stránky')} htmlFor="site-description">
          <textarea
            id="site-description"
            className={cn(FIELD, 'resize-none')}
            rows={2}
            value={advanced.pageDescription}
            onChange={(event) =>
              setAdvanced((current) => ({ ...current, pageDescription: event.target.value }))
            }
          />
          <Counter length={advanced.pageDescription.length} max={PAGE_DESCRIPTION_MAX} />
        </Labelled>
        <Labelled label={tt('Výška loga (px)')} htmlFor="site-logo-height">
          <input
            id="site-logo-height"
            type="number"
            min={LOGO_HEIGHT_MIN}
            max={LOGO_HEIGHT_MAX}
            className={FIELD}
            value={advanced.logoHeight}
            onChange={(event) =>
              setAdvanced((current) => ({ ...current, logoHeight: Number(event.target.value) }))
            }
          />
        </Labelled>
        <UrlInput
          id="site-home"
          label={tt('Úvodná stránka')}
          field="homePageUrl"
          value={advanced.homePageUrl}
          problem={problems.homePageUrl ?? null}
          onChange={(value) => setAdvanced((current) => ({ ...current, homePageUrl: value }))}
        />
      </Card>

      <Card title={tt('Odkazy a mapa')} icon={<Settings className="w-4 h-4 text-primary" />}>
        {(
          [
            { field: 'facebookUrl' as const, label: 'Facebook' },
            { field: 'instagramUrl' as const, label: 'Instagram' },
            { field: 'tiktokUrl' as const, label: 'TikTok' },
            { field: 'googleReviewsUrl' as const, label: tt('Hodnotenia Google') },
            { field: 'mapEmbedUrl' as const, label: tt('Mapa (odkaz z Google Máp)') },
          ]
        ).map((entry) => (
          <UrlInput
            key={entry.field}
            id={`site-${entry.field}`}
            label={entry.label}
            field={entry.field}
            value={social[entry.field]}
            problem={problems[entry.field] ?? null}
            onChange={(value) => set(entry.field, value)}
          />
        ))}
      </Card>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save-site
          disabled={!maySave || saveSocial.isPending || hasUrlProblem(problems)}
          onClick={save}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saveSocial.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
        </button>
        {hasUrlProblem(problems) && (
          <span className="text-[11px] text-red-500" data-save-blocked>
            <T>Opravte zvýraznené polia.</T>
          </span>
        )}
      </div>
    </div>
  )
}

function Counter({ length, max }: { length: number; max: number }) {
  return (
    <p className={cn('text-[11px]', length > max ? 'text-amber-500' : 'text-muted-foreground')}>
      {formatCount(length)}/{formatCount(max)}
      {length > max && (
        <>
          {' '}
          <T>- dlhší text vyhľadávač skráti</T>
        </>
      )}
    </p>
  )
}

function UrlInput({
  id,
  label,
  field,
  value,
  problem,
  onChange,
}: {
  id: string
  label: string
  field: UrlField
  value: string
  problem: UrlProblem
  onChange: (value: string) => void
}) {
  return (
    <Labelled label={label} htmlFor={id}>
      <input
        id={id}
        className={cn(FIELD, problem !== null && 'border-red-500')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {problem !== null && (
        <p className="text-[11px] text-red-500" data-url-problem={field}>
          {urlProblemMessage(field, problem)}
        </p>
      )}
    </Labelled>
  )
}

// ─── úvodná stránka ──────────────────────────────────────────────────────────

function HomepageTab() {
  const { data, isLoading } = useGetHomepageModules()
  if (isLoading || data === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  // Keyed on the server's own order, so a save or a delete remounts the editor with fresh state
  // rather than merging one into the other.
  const modules = data as HomepageModule[]
  return <HomepageEditor key={modules.map((module) => module.moduleKey).join('|')} modules={modules} />
}

function HomepageEditor({ modules }: { modules: HomepageModule[] }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const theme = useTheme()

  const server = useMemo(() => blocksOf(modules), [modules])
  const [blocks, setBlocks] = useState<Block[]>(() => server)
  const [open, setOpen] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Block | null>(null)
  const [adding, setAdding] = useState(false)

  const counts = blockCounts(blocks)
  const dirty = isDirty(blocks, server)
  const activeModules = theme.activeModules

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: getGetHomepageModulesQueryKey() })

  const saveAll = usePutHomepageModulesBulk({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Bloky uložené'), description: tt('Zmeny sú na zákazníckej stránke.') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const addBlock = usePostHomepageModules({
    mutation: {
      onSuccess: () => {
        invalidate()
        setAdding(false)
        toast({ title: tt('Blok pridaný') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Pridanie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server blok odmietol.')),
        }),
    },
  })

  const removeBlock = useDeleteHomepageModulesKey({
    mutation: {
      onSuccess: () => {
        invalidate()
        setConfirmDelete(null)
        toast({ title: tt('Blok zmazaný') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Mazanie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server mazanie odmietol.')),
        }),
    },
  })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-block-counts>
        <StatCard label={tt('Blokov')} value={formatCount(counts.total)} icon={Layers} />
        <StatCard
          label={tt('Zapnutých')}
          value={formatCount(counts.enabled)}
          icon={Eye}
          color="emerald"
        />
        <StatCard
          label={tt('Bez popisu polí')}
          value={formatCount(counts.unknown)}
          icon={AlertTriangle}
          color={counts.unknown > 0 ? 'amber' : 'emerald'}
        />
      </div>

      {/* FB-127: the checkbox list in the theme settings is not what composes the homepage. */}
      {activeModules.length > 0 && (
        <p className="text-[11px] text-amber-500" data-dead-setting>
          <T>
            V nastaveniach dizajnu je ešte starý zoznam „aktívnych modulov“ s
          </T>{' '}
          <strong>{formatCount(activeModules.length)}</strong>{' '}
          <T>
            položkami. Úvodnú stránku skladajú bloky nižšie, nie ten zoznam - nič ho nečíta. Tu je
            zapnutých
          </T>{' '}
          <strong>{formatCount(counts.enabled)}</strong> <T>blokov</T>.
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <button
            type="button"
            data-add-block
            onClick={() => setAdding(!adding)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-border text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> <T>Pridať blok</T> <ChevronDown className="w-3 h-3" />
          </button>
          {adding && (
            <div className="absolute z-20 mt-1 w-64 max-h-72 overflow-y-auto rounded-xl border border-border bg-card shadow-lg p-1" data-add-menu>
              {addableKeys().map((baseKey) => (
                <button
                  key={baseKey}
                  type="button"
                  data-add={baseKey}
                  disabled={addBlock.isPending}
                  onClick={() => addBlock.mutate({ data: { baseKey, enabled: false, content: {} } })}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted"
                >
                  {MODULE_SCHEMAS.find((schema) => schema.baseKey === baseKey)?.label ?? baseKey}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="flex-1" />
        {dirty && (
          <span className="text-[11px] text-amber-500 font-semibold" data-unsaved>
            {/* Toggles and reordering are local until this button is pressed; legacy says nothing. */}
            <T>Neuložené zmeny - zákazníci ich zatiaľ nevidia</T>
          </span>
        )}
        <button
          type="button"
          data-save-blocks
          disabled={!dirty || saveAll.isPending}
          onClick={() =>
            // The generated body is `{[key: string]: unknown}` because the spec does not describe it;
            // the endpoint reads an array, which `bulkPayload` produces and a test pins.
            saveAll.mutate({ data: bulkPayload(blocks) as unknown as PutHomepageModulesBulkBody })
          }
          className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saveAll.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť bloky</T>
        </button>
      </div>

      <div className="space-y-2" data-blocks>
        {blocks.map((block, index) => {
          const state = editorState(block.moduleKey)
          const schema = schemaFor(block.moduleKey)
          const expanded = open === block.moduleKey
          return (
            <div
              key={block.moduleKey}
              data-block={block.moduleKey}
              data-block-state={state}
              className={cn(
                'bg-card border rounded-2xl',
                block.enabled ? 'border-border' : 'border-border/50 opacity-70',
              )}
            >
              <div className="flex items-center gap-2 p-3 flex-wrap">
                <span className="text-xs text-muted-foreground tabular-nums w-6">{index + 1}.</span>
                <button
                  type="button"
                  data-toggle-block={block.moduleKey}
                  aria-label={`${block.enabled ? tt('Vypnúť') : tt('Zapnúť')} ${moduleLabel(block.moduleKey)}`}
                  onClick={() => setBlocks(toggle(blocks, block.moduleKey))}
                  className={cn(
                    'flex items-center px-2.5 py-1.5 rounded-xl border',
                    block.enabled ? 'border-emerald-500/50 text-emerald-500' : 'border-border text-muted-foreground',
                  )}
                >
                  {block.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{moduleLabel(block.moduleKey)}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate">
                    {block.moduleKey}
                  </p>
                </div>
                {state === 'unknown' && <StatusBadge label={tt('neznámy blok')} color="amber" />}
                {state === 'self-contained' && (
                  <StatusBadge label={tt('bez nastavení')} color="zinc" />
                )}
                <button
                  type="button"
                  data-move-up={block.moduleKey}
                  aria-label={`${tt('Posunúť vyššie')} ${moduleLabel(block.moduleKey)}`}
                  disabled={index === 0}
                  onClick={() => setBlocks(move(blocks, index, index - 1))}
                  className="p-1.5 rounded-lg border border-border disabled:opacity-30"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  data-move-down={block.moduleKey}
                  aria-label={`${tt('Posunúť nižšie')} ${moduleLabel(block.moduleKey)}`}
                  disabled={index === blocks.length - 1}
                  onClick={() => setBlocks(move(blocks, index, index + 1))}
                  className="p-1.5 rounded-lg border border-border disabled:opacity-30"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  data-open-block={block.moduleKey}
                  onClick={() => setOpen(expanded ? null : block.moduleKey)}
                  className="px-3 py-1.5 rounded-xl border border-border text-xs font-semibold"
                >
                  <T>Nastaviť</T>
                </button>
                <button
                  type="button"
                  data-delete-block={block.moduleKey}
                  aria-label={`${tt('Zmazať')} ${moduleLabel(block.moduleKey)}`}
                  onClick={() => setConfirmDelete(block)}
                  className="p-1.5 rounded-lg border border-border text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {expanded && (
                <div className="border-t border-border p-4 space-y-3" data-block-editor={block.moduleKey}>
                  {schema !== null && (
                    <p className="text-xs text-muted-foreground">{schema.description}</p>
                  )}
                  {state === 'unknown' ? (
                    // FB-128: legacy renders nothing here, exactly as it does for a finished block.
                    <p className="text-xs text-amber-500" data-unknown-block>
                      <T>
                        Tento blok nie je v katalógu, takže nevieme, aké polia má. Zákaznícka stránka ho
                        možno vykresľuje - nastaviť sa tu ale nedá nič. Nie je to to isté ako blok bez
                        nastavení.
                      </T>
                    </p>
                  ) : state === 'self-contained' ? (
                    <p className="text-xs text-muted-foreground" data-self-contained>
                      <T>Tento blok nemá čo nastavovať.</T>
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(schema?.fields ?? []).map((field) => (
                        <FieldInput
                          key={field.key}
                          field={field}
                          value={block.content[field.key]}
                          onChange={(value) =>
                            setBlocks(
                              setContent(blocks, block.moduleKey, {
                                ...block.content,
                                [field.key]: value,
                              }),
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={tt('Zmazať blok?')}
        description={
          confirmDelete === null
            ? ''
            : `${moduleLabel(confirmDelete.moduleKey)} - ${tt('blok aj jeho obsah sa odstránia zo zákazníckej stránky. Vrátiť sa to nedá.')}`
        }
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={removeBlock.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => removeBlock.mutate({ key: confirmDelete?.moduleKey ?? '' })}
      />
    </div>
  )
}

/** One field, drawn from its kind rather than from a hand-written form per module. */
function FieldInput({
  field,
  value,
  onChange,
}: {
  field: ModuleField
  value: unknown
  onChange: (value: unknown) => void
}) {
  const text = typeof value === 'string' ? value : ''

  if (field.kind === 'boolean') {
    const checked = value === true
    return (
      <label className="flex items-center gap-2 text-sm" data-field={field.key}>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={field.label}
          onClick={() => onChange(!checked)}
          className={cn(
            'w-9 h-5 rounded-full relative transition-colors',
            checked ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-4 h-4 rounded-full bg-background transition-all',
              checked ? 'left-4' : 'left-0.5',
            )}
          />
        </button>
        {field.label}
        {checked && <Check className="w-3.5 h-3.5 text-emerald-500" />}
      </label>
    )
  }

  if (field.kind === 'image') {
    return <ImageField field={field} value={text} onChange={onChange} />
  }

  if (field.kind === 'products') {
    return <ProductsField field={field} value={value} onChange={onChange} />
  }

  // **The placeholder reaches the box - §9.329.** The schema has carried a `placeholder?` since it
  // was written and nothing passed it on, so 30 fields ported from legacy showed an empty box under
  // a terse label. `aria-label` as well: a `<label htmlFor>` covers the sighted case, and the e2e
  // suite addresses these boxes by accessible name.
  const common = {
    id: `field-${field.key}`,
    className: FIELD,
    placeholder: field.placeholder,
    'aria-label': field.label,
  }

  return (
    <div className={cn('space-y-1', field.kind === 'multiline' && 'sm:col-span-2')} data-field={field.key}>
      <label className="text-xs font-bold text-muted-foreground" htmlFor={common.id}>
        {field.label}
      </label>
      {field.kind === 'multiline' ? (
        <textarea
          {...common}
          className={cn(FIELD, 'resize-none')}
          rows={field.rows ?? 3}
          value={text}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : field.kind === 'number' ? (
        <input
          {...common}
          type="number"
          min={field.min}
          max={field.max}
          value={typeof value === 'number' ? value : ''}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      ) : field.kind === 'colour' ? (
        <div className="flex items-center gap-2">
          <input
            {...common}
            value={text}
            placeholder="#f97316"
            onChange={(event) => onChange(event.target.value)}
          />
          <span
            className="w-8 h-8 rounded-lg border border-border shrink-0"
            style={{ background: text === '' ? 'transparent' : text }}
          />
        </div>
      ) : (
        <input {...common} value={text} onChange={(event) => onChange(event.target.value)} />
      )}
      {field.hint !== undefined && (
        <p className="text-[11px] text-muted-foreground">{field.hint}</p>
      )}
    </div>
  )
}

/**
 * An image field that uploads one - §9.329.
 *
 * **The owner:** *"Settings are not completed, like uploading image, they looking shaby"*. This
 * field kind fell through to `FieldInput`'s plain text input, so configuring the homepage banner
 * meant **pasting a URL** into a box labelled *Obrázok - počítač*. There was no upload, no preview
 * and no way to tell a wrong path from an empty one.
 *
 * Legacy's `ModuleImageUpload` is the shape being ported: drag or click, a preview with *Zmeniť* and
 * *Odstrániť*, the two checks in `imageProblem`, and the accepted formats stated. The endpoint
 * already existed - `usePostUploadImage`, which the burger-box slot editor has used since FB-022 -
 * so nothing new was built on the server.
 *
 * <p>FB-022's lesson is kept: legacy swallows a failed upload with `catch {}` and the button just
 * returns to rest, so a 200 with no `url` says so here rather than looking like success.
 */
function ImageField({
  field,
  value,
  onChange,
}: {
  field: ModuleField
  value: string
  onChange: (value: unknown) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const uploadImage = usePostUploadImage()
  const { toast } = useToast()

  const accept = (file: File | undefined) => {
    if (file === undefined) return
    const problem = imageProblem(file)
    if (problem !== null) {
      toast({
        variant: 'destructive',
        title: tt('Obrázok sa nedá použiť'),
        description: imageProblemMessage(problem),
      })
      return
    }
    const form = new FormData()
    form.append('image', file)
    uploadImage.mutate(
      { data: form as unknown as { image: Blob } },
      {
        onSuccess: (response) => {
          const url = (response as { url?: string } | undefined)?.url
          if (url !== undefined && url !== '') {
            onChange(url)
            toast({ title: tt('Obrázok nahraný') })
            return
          }
          toast({
            variant: 'destructive',
            title: tt('Nahrávanie zlyhalo'),
            description: tt('Server nevrátil adresu obrázka.'),
          })
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Nahrávanie zlyhalo'),
            description: apiErrorMessage(caught, tt('Obrázok sa nepodarilo nahrať.')),
          }),
      },
    )
  }

  return (
    <div className="space-y-1.5 sm:col-span-2" data-field={field.key} data-image-field={field.key}>
      <label className="text-xs font-bold text-muted-foreground">{field.label}</label>
      {field.hint !== undefined && (
        <p className="text-[11px] text-muted-foreground">{field.hint}</p>
      )}

      {value === '' ? (
        <button
          type="button"
          aria-label={field.label}
          onClick={() => fileInput.current?.click()}
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            accept(event.dataTransfer.files[0])
          }}
          disabled={uploadImage.isPending}
          data-image-empty
          className={cn(
            'w-full rounded-xl border-2 border-dashed p-6 text-center transition-all',
            dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30',
          )}
        >
          {uploadImage.isPending ? (
            <Spinner className="w-6 h-6 mx-auto mb-1.5" />
          ) : (
            <Upload className="w-6 h-6 mx-auto mb-1.5 text-muted-foreground" />
          )}
          <p className="text-sm font-semibold text-foreground">
            {uploadImage.isPending ? tt('Nahrávam...') : tt('Kliknite alebo presuňte súbor')}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">PNG, JPG, WebP · max. 5 MB</p>
        </button>
      ) : (
        <div className="space-y-2" data-image-set>
          <img
            src={value}
            alt={field.label}
            className="w-full max-h-40 object-cover rounded-xl border border-border bg-muted/30"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-image-replace
              onClick={() => fileInput.current?.click()}
              disabled={uploadImage.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              {uploadImage.isPending ? (
                <Spinner className="w-3 h-3" />
              ) : (
                <Upload className="w-3 h-3" />
              )}
              <T>Zmeniť</T>
            </button>
            <button
              type="button"
              data-image-clear
              onClick={() => onChange('')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500/20 transition-colors"
            >
              <X className="w-3 h-3" />
              <T>Odstrániť</T>
            </button>
          </div>
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          accept(event.target.files?.[0])
          event.target.value = ''
        }}
      />
    </div>
  )
}

/**
 * The product picker - §9.329.
 *
 * **This field said so itself**: *"výber produktov sa tu zatiaľ nedá meniť"*. Two blocks - *Obľúbené
 * jedlá* and *Odporúčania šéfkuchára* - have `productIds` as their only meaningful setting, so both
 * were unconfigurable, which is a fair share of *"these bloks are still not working properly"*.
 *
 * Legacy's shape: a search box and every product as a toggle chip, grouped by category. What is added
 * over legacy is `selectionNote` - a stored id whose product has since been deleted is **counted and
 * named** rather than silently dropped, because a count that disagrees with the chips on screen is
 * how somebody concludes a picker is broken.
 */
function ProductsField({
  field,
  value,
  onChange,
}: {
  field: ModuleField
  value: unknown
  onChange: (value: unknown) => void
}) {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useGetProducts()

  const products = useMemo<PickerProduct[]>(
    () =>
      (data ?? [])
        .filter((product): product is Product & { id: number } => typeof product.id === 'number')
        .map((product) => ({
          id: product.id,
          name: product.name ?? `#${product.id}`,
          categoryName: product.categoryName ?? null,
          active: product.active,
        })),
    [data],
  )

  const ids = productIdsOf(value)
  const groups = useMemo(() => productGroups(products, search), [products, search])

  return (
    <div className="space-y-1.5 sm:col-span-2" data-field={field.key} data-products-field={field.key}>
      <label className="text-xs font-bold text-muted-foreground" htmlFor={`search-${field.key}`}>
        {field.label}
      </label>
      <p className="text-[11px] text-muted-foreground" data-products-note>
        {selectionNote(ids, products)}
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground text-xs">
          <Spinner className="w-4 h-4" /> <T>Načítavam produkty...</T>
        </div>
      ) : (
        <>
          <input
            id={`search-${field.key}`}
            value={search}
            aria-label={`${field.label} - ${tt('hľadať')}`}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Hľadať produkt...')}
            className={FIELD}
          />
          <div className="max-h-64 overflow-y-auto space-y-3 border border-border rounded-xl p-2 bg-muted/10">
            {groups.length === 0 && (
              <p className="text-xs text-muted-foreground px-1 py-2" data-products-empty>
                <T>Žiadny produkt nezodpovedá hľadaniu.</T>
              </p>
            )}
            {groups.map((group) => (
              <div key={group.category}>
                <p className="text-[11px] font-bold text-muted-foreground mb-1 sticky top-0 bg-muted/80 px-1 py-0.5 rounded">
                  {group.category}
                </p>
                <div className="flex flex-wrap gap-1">
                  {group.products.map((product) => {
                    const selected = ids.includes(product.id)
                    return (
                      <button
                        key={product.id}
                        type="button"
                        data-product-chip={product.id}
                        aria-pressed={selected}
                        onClick={() => onChange(toggleProductId(ids, product.id))}
                        className={cn(
                          'text-xs px-2 py-1 rounded-lg border transition-colors',
                          selected
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border text-foreground hover:border-primary/50',
                          product.active === false && 'opacity-60',
                        )}
                      >
                        {product.name}
                        {/* Kept in the list rather than hidden: a block that already names an
                            inactive product is the case where hiding it would look like data loss. */}
                        {product.active === false ? ` (${tt('neaktívny')})` : ''}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── the small shared pieces ─────────────────────────────────────────────────

function Card({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</p>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

function Labelled({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  )
}
